/** tm/legal/LegalCabinetDetail.tsx — 국무회의 의결 상세 (GET /cabinet/:id): 법령 종류·소관 부처·국무회의(회차)·현재 단계·공포/국회 제출 ·
 *  제·개정 이유·주요내용 · 「진행 단계」(입법예고 → 국무회의 → 국회 → 공포 → 시행, 이어진 글로 이동) · 원문(정부입법현황)·현행 법령 · 별표 · 안 읽음으로 */
import { useCallback, useEffect, useState } from 'react';
import { C, T } from '../components/ui';
import { getCabinet, setCabinetRead, setCabinetStar } from './legalApi';
import { btn, shortDate, safeHttp, errMsg, lawKindLabel, lawUrl, cabinetStatus, AMBER, MUTED } from './legalTheme';
import { DetailHead, MetaGrid, Box, DetailEmpty, DetailSkeleton, DETAIL_MAX, actsRow } from './LegalDetailParts';
import LegalStageTimeline from './LegalStageTimeline';
import type { CabinetDetail, Selection } from './legalTypes';

interface Props {
  id: number; mobile: boolean;
  onBack: () => void; onToast: (m: string, k?: 'ok' | 'error') => void;
  onPatch: (p: { starred?: boolean; unread?: boolean }) => void;
  onOpen?: (s: NonNullable<Selection>) => void;
}

function StatusBadge({ status }: { status: string | null }) {
  const c = cabinetStatus(status);
  return <span style={{ fontSize: T.sm, fontWeight: 700, padding: '2px 6px', borderRadius: 10, whiteSpace: 'nowrap', background: c.bg, color: c.fg }}>{c.text}</span>;
}

/** 요약 → [제목, 본문] 묶음 (수집기가 «제·개정 이유\n…\n\n주요내용\n…» 로 저장) */
function sectionsOf(summary: string): [string, string][] {
  const out: [string, string][] = [];
  for (const part of summary.split(/\n{2,}(?=(?:제·개정 이유|주요내용)\n)/)) {
    const m = part.match(/^(제·개정 이유|주요내용)\n([\s\S]*)$/);
    if (m && m[2].trim()) out.push([m[1], m[2].trim()]);
    else if (part.trim()) out.push(['제·개정 이유 · 주요내용', part.trim()]);
  }
  return out;
}

/** 다음에 일어날 일 한 줄 — 법률안은 국회로, 시행령·시행규칙은 공포로 */
function nextHint(c: CabinetDetail): string {
  if (c.promulgated_date) return '공포되었습니다. 시행일·바뀐 조문은 「법령」에서 확인하세요.';
  if (c.assembly_bill_no) return '국회에 제출되었습니다. 이후 심사·가결 상태는 「국회 법안」에서 이어서 알려 드립니다.';
  if (/법률/.test(c.law_kind || '')) return '국무회의를 통과한 법률안은 국회에 제출되어 심사를 받습니다.';
  return '국무회의를 통과한 시행령은 보통 1~2주 안에 공포됩니다. 공포되면 「법령」에 올라옵니다.';
}

export default function LegalCabinetDetail({ id, mobile, onBack, onToast, onPatch, onOpen }: Props) {
  const [c, setC] = useState<CabinetDetail | null>(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    setC(null); setErr('');
    try { setC(await getCabinet(id)); } catch (e) { setErr(errMsg(e, '국무회의 의결 법령안을 불러오지 못했습니다')); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  if (err) return <>{mobile && <button type="button" className="lw-f" onClick={onBack} style={btn()}>← 목록</button>}<DetailEmpty error={err} onRetry={load} /></>;
  if (!c) return <DetailSkeleton />;

  const star = async () => {
    const next = !c.starred;
    setC({ ...c, starred: next }); onPatch({ starred: next });
    try { await setCabinetStar(c.id, next); onToast(next ? '별표했습니다' : '별표를 해제했습니다'); }
    catch (e) { setC({ ...c, starred: !next }); onPatch({ starred: !next }); onToast(errMsg(e, '별표하지 못했습니다'), 'error'); }
  };
  const unread = async () => {
    onPatch({ unread: true });
    try { await setCabinetRead(c.id, false); onToast('안 읽음으로 바꿨습니다'); }
    catch (e) { onPatch({ unread: false }); onToast(errMsg(e, '바꾸지 못했습니다'), 'error'); }
  };
  const url = safeHttp(c.detail_url);
  const kind = lawKindLabel(c.law_kind);
  const noLawPage = /(\.\.\.|…)$/.test(c.title) || (/제정/.test(c.amend_kind || '') && !c.promulgated_date); // 잘린 이름 · 아직 없는 새 법령

  return (
    <div>
      <DetailHead group="국무회의" label="국무회의 의결" topics={c.topics || []} title={c.title} mobile={mobile} onBack={onBack} stage="cabinet"
        extra={<StatusBadge status={c.status} />} />
      <MetaGrid mobile={mobile} rows={[
        ['법령 종류', `${kind || '-'}${c.law_kind && kind !== c.law_kind ? ` (${c.law_kind})` : ''}${c.amend_kind ? ` · ${c.amend_kind}` : ''}`],
        ['소관 부처', c.ministry || '-'],
        ['국무회의', <>{shortDate(c.cabinet_date)}{c.cabinet_no ? <span style={{ color: C.sub }}> · 제{c.cabinet_no}회</span> : null}</>],
        ['현재 단계', <StatusBadge status={c.status} />],
        ...(c.promulgated_date ? [['공포', `${shortDate(c.promulgated_date)}${c.promulgation_no ? ` · ${c.promulgation_no}` : ''}`] as [string, string]] : []),
        ...(c.assembly_bill_no ? [['국회 제출', `${shortDate(c.submitted_date)} · 의안번호 ${c.assembly_bill_no}`] as [string, string]] : []),
      ]} />
      {c.summary
        ? sectionsOf(c.summary).map(([t, body], i) => <Box key={`${t}${i}`} kind={i === 0 ? 'main' : 'q'} title={t}>{body}</Box>)
        : <Box kind="q" title="제·개정 이유 · 주요내용">요약이 아직 없습니다. 「정부입법현황 원문」에서 확인해 주세요.</Box>}
      <LegalStageTimeline kind="cabinet" id={c.id} onOpen={onOpen} hideSource />
      <p style={{ fontSize: T.sm, color: MUTED, maxWidth: DETAIL_MAX, lineHeight: 1.6, margin: '0 0 12px' }}>
        {nextHint(c)}{(c.stages || []).length ? '' : ' 출처: 법제처 국민참여입법센터 「정부입법현황」.'}
      </p>
      <div style={actsRow}>
        {url && <a className="lw-f" href={url} target="_blank" rel="noopener noreferrer" style={btn('pri')}>정부입법현황 원문</a>}
        {!noLawPage && <a className="lw-f" href={lawUrl(c.title)} target="_blank" rel="noopener noreferrer" style={btn()}>현행 법령</a>}
        <button type="button" className="lw-f" onClick={star} aria-pressed={c.starred} style={{ ...btn(), color: c.starred ? AMBER : C.sub }}>{c.starred ? '★ 별표 해제' : '☆ 별표'}</button>
        <button type="button" className="lw-f" onClick={unread} style={btn()}>안 읽음으로</button>
      </div>
    </div>
  );
}
